import './JoinDiscussionOverlay.css'

import React, { useEffect, useState } from 'react'

import { useLogger } from '../logger'
import { useEventTracker } from '../providers/event-tracker-provider'

interface JoinCommentDiscussionOverlayProps {
  forumName: string
  commentId: string
  onClose: () => void
}

const JoinCommentDiscussionOverlay: React.FC<JoinCommentDiscussionOverlayProps> = ({
  forumName,
  commentId,
  onClose,
}) => {
  const [copied, setCopied] = useState(false)
  const logger = useLogger()
  const { tracker: eventTracker } = useEventTracker()

  // Check if user just came from nugle.link or nugle.ai
  // If they did, Universal Link failed to intercept → app not installed
  const referrer = document.referrer.toLowerCase()
  const cameFromNugleLink = referrer.includes('nugle.link') || referrer.includes('nugle.ai')

  useEffect(() => {
    if (cameFromNugleLink) {
      logger.info('User came from Nugle site - app likely not installed', { referrer })
      eventTracker.track({ name: 'Link Comment Overlay Shown - App Not Installed', referrer })
    }
  }, [cameFromNugleLink, logger, eventTracker, referrer])

  // New scheme: embed commentId in code param value
  const referralCode = `${forumName}?commentId=${commentId}`
  const deepLink = `nugle://?code=${encodeURIComponent(referralCode)}`

  // Format the invite code to show only the beginning with ellipses
  const formatInviteCode = (code: string) => {
    if (code.length < 20) {
      return code
    }
    return code.substring(0, 20) + '...'
  }

  const handleCopyClick = async () => {
    eventTracker.track({ name: 'Link Comment CopyInvite Pressed', forumName, commentId })
    try {
      await navigator.clipboard.writeText(referralCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      logger.error('Error copying text: ', err)
    }
  }

  const handleGoClick = () => {
    eventTracker.track({
      name: 'Link Comment Go Pressed',
      forumName,
      commentId,
      cameFromNugleLink,
    })

    // Use Universal Link (HTTPS) instead of custom URL scheme
    // Using nugle.ai because that's what the current App Store version has in associatedDomains
    const universalLink = `https://nugle.ai/comment/${encodeURIComponent(referralCode)}`
    const customSchemeLink = deepLink // Keep existing custom scheme as fallback

    // App Store URLs
    const appStoreUrl = 'https://apps.apple.com/sg/app/nugle-be-heard/id6745102771'
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=ai.nugle.app'

    logger.info('Attempting to open universal link:', { universalLink, cameFromNugleLink })

    // Detect if user is on iOS or Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isAndroid = /Android/.test(navigator.userAgent)

    // If user came from nugle.link/nugle.ai, they already tried Universal Link
    // and it didn't intercept → app likely not installed
    // Skip the waiting and redirect to App Store faster
    if (cameFromNugleLink) {
      logger.info('User came from Nugle site - skipping Universal Link attempt, going to App Store')

      if (isIOS) {
        window.location.href = appStoreUrl
      } else if (isAndroid) {
        window.location.href = playStoreUrl
      } else {
        window.location.href = appStoreUrl
      }
      return
    }

    let appOpened = false

    // Listen for page visibility to detect if app opened
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true
        logger.info('App likely opened (page became hidden)')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Try Universal Link first (preferred method)
    window.location.href = universalLink

    // Fallback: If still on page after 3 seconds, show store options
    const fallbackTimeout = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      if (!appOpened && !document.hidden) {
        logger.info('App not installed, trying custom scheme as fallback')

        // Try custom scheme as secondary fallback
        window.location.href = customSchemeLink

        // If still on page after another 1.5 seconds, redirect to store
        setTimeout(() => {
          if (!document.hidden) {
            logger.info('Redirecting to app store')
            if (isIOS) {
              window.location.href = appStoreUrl
            } else if (isAndroid) {
              window.location.href = playStoreUrl
            } else {
              window.location.href = appStoreUrl
            }
          }
        }, 1500)
      }
    }, 3000)

    // Cleanup
    return () => {
      clearTimeout(fallbackTimeout)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  // Track when overlay is shown
  useEffect(() => {
    eventTracker.track({ name: 'Link Comment Overlay Shown', forumName, commentId })
  }, [eventTracker, forumName, commentId])

  // Close overlay when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('overlay-backdrop')) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  return (
    <div className="overlay-backdrop">
      <div className="join-overlay">
        <h2>Join the discussion</h2>

        <div className="step">
          <div className="step-content">
            <p>1. Copy group code</p>
            <p className="step-subtitle">(if you haven&apos;t installed Nugle yet)</p>

            <div className="code-container">
              <div className="invite-code">{formatInviteCode(referralCode)}</div>
              <button className="copy-button" onClick={handleCopyClick} aria-label="Copy group code">
                {copied ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="step">
          <div className="step-content">
            <p>2. 👇</p>
          </div>
        </div>

        <button className="go-button" onClick={handleGoClick}>
          Go
        </button>
      </div>
    </div>
  )
}

export default JoinCommentDiscussionOverlay
